<div class="voyage-container" [ngClass]="{ 'dark-mode': isDarkMode() }">

  <!-- ================= GLOBAL DRAG OVERLAY ================= -->
  <div class="global-drag-overlay" *ngIf="isDraggingCsvOver && canEditForm">
    <div class="global-drag-card">
      <div class="global-drag-icon-circle">
        <mat-icon class="global-drag-icon">cloud_upload</mat-icon>
      </div>
      <h3 class="global-drag-title">Drop route waypoint file</h3>
      <p class="global-drag-sub">Supports CSV, TXT, Excel waypoint lists (Max 10MB)</p>
    </div>
  </div>

  <!-- ================= READ-ONLY WARNING BANNER ================= -->
  <div class="readonly-banner" *ngIf="isEditMode && !canEditForm">
    <mat-icon>lock</mat-icon>
    <div class="banner-content">
      <strong>Read-Only Mode:</strong> This request is currently being routed by Meraki Operations (Status: {{
      editingItemStatus }}). Editing is locked unless Meraki requests customer revisions.
    </div>
  </div>

  <!-- ================= TOP HEADER ================= -->
  <header class="page-header">
    <div class="header-left">
      <div class="title-row">
        <div class="header-icon-wrapper" [class.edit-mode-icon]="isEditMode">
          <mat-icon class="header-main-icon">{{ isEditMode ? 'edit_note' : 'directions_boat' }}</mat-icon>
        </div>
        <div>
          <div class="title-with-badge">
            <h1 class="main-title">{{ formPageHeading }}</h1>
            <span class="badge-status-pill" *ngIf="isEditMode && editingItemStatus">
              {{ editingItemStatus }}
            </span>
            <span class="badge-beta" *ngIf="!isEditMode">Beta</span>
          </div>
          <p class="subtitle">Configure voyage parameters, interactive waypoints, and speed profiles for route
            optimization.</p>
        </div>
      </div>
    </div>

    <div class="header-right">
      <button type="button" mat-stroked-button class="view-requests-btn" (click)="goToList()">
        <mat-icon>list_alt</mat-icon>
        <span>{{ requestsPageTitle }}</span>
      </button>

      <button type="button" mat-stroked-button class="header-draft-btn" (click)="saveAsDraft()"
        [disabled]="isSavingDraft || isSubmittingForm"
        *ngIf="canEditForm && (!isEditMode || editingItemStatus === 'Draft')">
        <mat-spinner diameter="16" class="btn-spinner" *ngIf="isSavingDraft"></mat-spinner>
        <mat-icon *ngIf="!isSavingDraft">save_as</mat-icon>
        <span>{{ isSavingDraft ? 'Saving Draft…' : 'Save as Draft' }}</span>
      </button>

      <button type="button" mat-stroked-button class="header-reset-btn" (click)="resetForm()"
        [disabled]="isSavingDraft || isSubmittingForm" *ngIf="canEditForm && !isEditMode">
        <mat-icon>restart_alt</mat-icon>
        <span>Clear</span>
      </button>

      <button type="button" mat-flat-button class="header-submit-btn" (click)="onSubmit()"
        [disabled]="isSavingDraft || isSubmittingForm" *ngIf="canEditForm">
        <mat-spinner diameter="16" class="btn-spinner submit-spinner" *ngIf="isSubmittingForm"></mat-spinner>
        <mat-icon *ngIf="!isSubmittingForm">{{ editingItemStatus === 'Edit Requested' ? 'published_with_changes' :
          (isEditMode && editingItemStatus !== 'Draft' ? 'save' : 'send') }}</mat-icon>
        <span>{{ isSubmittingForm ? 'Submitting…' : (editingItemStatus === 'Edit Requested' ? 'Resubmit Request' :
          (isEditMode && editingItemStatus !== 'Draft' ? 'Save Changes' : (editingItemStatus === 'Draft' ? 'Submit
          Draft' : 'Submit Request'))) }}</span>
      </button>
    </div>
  </header>

  <div class="form-wrapper">
    <form [formGroup]="voyageForm">

      <!-- ================= 1. VESSEL DETAILS ================= -->
      <section class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <mat-icon class="section-icon">directions_boat</mat-icon>
            <div>
              <h2 class="section-title">Vessel Details</h2>
              <p class="section-subtitle">Select vessel or enter details to automatically pull specifications</p>
            </div>
          </div>
        </div>

        <!-- Custom Vessel Active Banner -->
        <div class="custom-port-active-bar custom-vessel-bar" *ngIf="vesselIsOthers">
          <div class="custom-port-tag">
            <mat-icon class="custom-port-icon">add_box</mat-icon>
            <span class="custom-port-title">Custom Vessel Mode:</span>
            <span class="custom-port-desc">Enter vessel name and IMO manually</span>
          </div>
          <button type="button" class="back-to-search-btn" (click)="cancelVesselOthers()" *ngIf="canEditForm">
            <mat-icon>search</mat-icon>
            <span>Select From Directory Instead</span>
          </button>
        </div>

        <div class="form-grid grid-3-col">
          <!-- Standard Vessel Name (Autocomplete) -->
          <mat-form-field appearance="outline" class="voyage-form-field" *ngIf="!vesselIsOthers">
            <mat-label>Vessel Name</mat-label>
            <input matInput formControlName="vesselName" required placeholder="Search by vessel name or IMO"
              [matAutocomplete]="vesselAuto" (input)="onVesselNameInput($event)" (focus)="onVesselFocus()" />
            <mat-spinner *ngIf="isLoadingVessels" matSuffix diameter="18" class="vessel-field-spinner"></mat-spinner>
            <mat-icon *ngIf="!isLoadingVessels" matSuffix class="field-suffix-icon">search</mat-icon>
            <mat-autocomplete #vesselAuto="matAutocomplete" panelClass="voyage-dropdown-panel"
              (optionSelected)="onVesselSelected($event.option.value)">
              <mat-option *ngIf="isLoadingVessels && vesselSuggestions.length === 0" disabled
                class="loading-vessels-option">
                <div class="autocomplete-loading-row">
                  <mat-spinner diameter="16"></mat-spinner>
                  <span>Loading vessel directory…</span>
                </div>
              </mat-option>
              <mat-option *ngFor="let vessel of vesselSuggestions" [value]="vessel.vessel_name">
                <div class="autocomplete-option-row">
                  <mat-icon class="option-icon">directions_boat</mat-icon>
                  <span class="option-title">{{ vessel.vessel_name }}</span>
                  <span class="option-imo" *ngIf="vessel.imo"> IMO: {{ vessel.imo }}</span>
                </div>
              </mat-option>
              <!-- Others Custom Vessel Option -->
              <mat-option *ngIf="shouldShowOthersVessel()" value="Others" class="others-custom-option">
                <div class="autocomplete-option-row others-option-row">
                  <div class="others-title-wrap">
                    <mat-icon class="others-icon">add_box</mat-icon>
                    <span class="option-title">Others (Vessel Not In List)</span>
                  </div>
                  <span class="option-country others-hint">Enter vessel name & IMO manually</span>
                </div>
              </mat-option>
            </mat-autocomplete>
            <mat-error *ngIf="voyageForm.get('vesselName')?.hasError('required')">
              Vessel Name is required
            </mat-error>
          </mat-form-field>

          <!-- Custom Vessel Name (When Others selected) -->
          <mat-form-field appearance="outline" class="voyage-form-field" *ngIf="vesselIsOthers">
            <mat-label>Custom Vessel Name</mat-label>
            <input matInput formControlName="customVesselName" required placeholder="e.g. STAR PACIFIC" />
            <mat-icon matSuffix class="field-suffix-icon">directions_boat</mat-icon>
            <mat-error *ngIf="voyageForm.get('customVesselName')?.invalid">
              Vessel Name is required
            </mat-error>
          </mat-form-field>

          <!-- IMO Number -->
          <mat-form-field appearance="outline" class="voyage-form-field vessel-field-with-status">
            <mat-label>IMO Number</mat-label>
            <input matInput formControlName="imo" [readonly]="!vesselIsOthers" placeholder="e.g. 9876543" />
            <mat-spinner *ngIf="isLoadingVesselDetails" matSuffix diameter="18"
              class="vessel-field-spinner"></mat-spinner>
            <mat-icon *ngIf="!isLoadingVesselDetails && voyageForm.get('imo')?.value" matSuffix
              class="field-success-icon">
              verified
            </mat-icon>
          </mat-form-field>

          <!-- Type of Vessel -->
          <mat-form-field appearance="outline" class="voyage-form-field vessel-field-with-status">
            <mat-label>Type of Vessel</mat-label>
            <input matInput formControlName="vesselType" placeholder="e.g. Bulk Carrier / Tanker" />
            <mat-hint *ngIf="isLoadingVesselDetails">Loading vessel specifications…</mat-hint>
            <mat-hint *ngIf="!isLoadingVesselDetails && vesselTypeAutoPopulated" class="auto-fill-hint">
              <mat-icon class="hint-icon">auto_awesome</mat-icon> Auto-filled from quick onboarding
            </mat-hint>
            <mat-error *ngIf="voyageForm.get('vesselType')?.invalid">
              Vessel Type is required
            </mat-error>
          </mat-form-field>

          <!-- Year Built -->
          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Year Built</mat-label>
            <input matInput formControlName="yearBuilt" type="number" placeholder="e.g. 2018" />
            <mat-icon matSuffix class="field-suffix-icon">event</mat-icon>
          </mat-form-field>

          <!-- Vessel Email -->
          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Vessel Email</mat-label>
            <input matInput formControlName="vesselEmail" type="email" placeholder="master@vessel.com" />
            <mat-icon matSuffix class="field-suffix-icon">email</mat-icon>
            <mat-error *ngIf="voyageForm.get('vesselEmail')?.hasError('required')">
              Email is required
            </mat-error>
            <mat-error *ngIf="voyageForm.get('vesselEmail')?.hasError('email')">
              Invalid email format
            </mat-error>
          </mat-form-field>

          <!-- Operating Condition -->
          <mat-form-field appearance="outline" class="voyage-form-field condition-select-field">
            <mat-label>Operating Condition</mat-label>
            <mat-select formControlName="condition" panelClass="voyage-dropdown-panel">
              <mat-option value="Laden">
                <mat-icon class="option-icon">layers</mat-icon>
                <span>Laden (Loaded)</span>
              </mat-option>
              <mat-option value="Ballast">
                <mat-icon class="option-icon">water</mat-icon>
                <span>Ballast (Empty)</span>
              </mat-option>
            </mat-select>
            <mat-icon matSuffix class="field-suffix-icon">tune</mat-icon>
          </mat-form-field>
        </div>
      </section>

      <!-- ================= 2. VOYAGE SCHEDULE ================= -->
      <section class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <mat-icon class="section-icon">calendar_today</mat-icon>
            <div>
              <h2 class="section-title">Voyage Schedule & Voyage Number</h2>
              <p class="section-subtitle">Set voyage number and scheduled departure date & time (ETD)</p>
            </div>
          </div>
        </div>

        <div class="form-grid grid-3-col">
          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Voyage Number</mat-label>
            <input matInput formControlName="voyageRef" placeholder="e.g. VOY-001" />
            <mat-icon matSuffix class="field-suffix-icon">tag</mat-icon>
          </mat-form-field>

          <!-- Departure Date with Material Datepicker & ±7 Days Bound -->
          <mat-form-field appearance="outline" class="voyage-form-field date-picker-field">
            <mat-label>Departure Date (ETD)</mat-label>
            <input matInput [matDatepicker]="departureDatePicker" formControlName="departureDate"
              [min]="minDepartureDateObj" [max]="maxDepartureDateObj" placeholder="Select date (±7 days)" required
              readonly (click)="departureDatePicker.open()" />
            <mat-datepicker-toggle matIconSuffix [for]="departureDatePicker">
              <mat-icon matDatepickerToggleIcon class="calendar-toggle-icon">event</mat-icon>
            </mat-datepicker-toggle>
            <mat-datepicker #departureDatePicker panelClass="custom-voyage-datepicker"></mat-datepicker>
            <mat-hint class="date-window-hint">Allowed departure window: {{ minDepartureDate }} to {{ maxDepartureDate
              }} (±7 days)</mat-hint>
            <mat-error *ngIf="voyageForm.get('departureDate')?.hasError('required')">
              Departure Date is required
            </mat-error>
            <mat-error *ngIf="voyageForm.get('departureDate')?.hasError('dateOutOfRange')">
              Date must be within ±7 days of today ({{ minDepartureDate }} to {{ maxDepartureDate }})
            </mat-error>
          </mat-form-field>

          <!-- Departure Time -->
          <mat-form-field appearance="outline" class="voyage-form-field time-picker-field">
            <mat-label>Departure Time (UTC)</mat-label>
            <input matInput type="time" formControlName="departureTime" required />
            <mat-icon matSuffix class="field-suffix-icon">schedule</mat-icon>
            <mat-error *ngIf="voyageForm.get('departureTime')?.invalid">
              Departure Time is required
            </mat-error>
          </mat-form-field>
        </div>
      </section>

      <!-- ================= 3. VOYAGE ROUTE & WAYPOINTS ================= -->
      <section class="section-card route-section-card">
        <div class="section-header">
          <div class="section-header-left">
            <mat-icon class="section-icon route-icon">alt_route</mat-icon>
            <div>
              <div class="title-with-pill">
                <h2 class="section-title">Voyage Route & Waypoints</h2>
                <span class="route-count-pill" *ngIf="itinerary.length > 0">
                  {{ itinerary.length }} Waypoint{{ itinerary.length === 1 ? '' : 's' }}
                </span>
              </div>
              <p class="section-subtitle">
                Departure is fixed at the origin. Reorder intermediate waypoints and destination via drag-and-drop (⠿)
                or using the Up/Down buttons.
              </p>
            </div>
          </div>

          <div class="section-header-right" *ngIf="canEditForm">
            <button type="button" mat-flat-button class="add-mid-port-top-btn" (click)="addItinerary()">
              <mat-icon>add_location_alt</mat-icon>
              <span>Add Waypoint</span>
            </button>
          </div>
        </div>

        <div class="route-timeline-container">

          <!-- 3.1 FIXED DEPARTURE PORT -->
          <div class="timeline-stop-card departure-card">
            <!-- Left Fixed Indicator -->
            <div class="stop-reorder-column fixed-origin-col">
              <div class="fixed-anchor-badge" matTooltip="Departure Port is fixed and immovable (Origin)"
                matTooltipPosition="above">
                <mat-icon class="anchor-icon">anchor</mat-icon>
              </div>
            </div>

            <!-- Stop Node Marker -->
            <div class="stop-node-column">
              <div class="node-marker departure-marker">
                <span class="node-dot"></span>
              </div>
              <div class="node-connector-line"></div>
            </div>

            <!-- Stop Body -->
            <div class="stop-body-container">
              <div class="stop-header-row">
                <div class="stop-badge-group">
                  <span class="stop-badge origin-badge">
                    <mat-icon class="badge-icon">anchor</mat-icon>
                    ORIGIN
                  </span>
                  <span class="stop-title">Departure Port (Fixed)</span>
                </div>
                <span class="stop-subtext">Voyage starting point (ETD)</span>
              </div>

              <!-- Others Custom Port Mode Banner -->
              <div class="custom-port-active-bar" *ngIf="startIsOthers">
                <div class="custom-port-tag">
                  <mat-icon class="custom-port-icon">add_location_alt</mat-icon>
                  <span class="custom-port-title">Custom Port Mode:</span>
                  <span class="custom-port-desc">Custom Port Name required &plus; (Coordinates <strong>OR</strong>
                    UN/LOCODE required)</span>
                </div>
                <button type="button" class="back-to-search-btn" (click)="cancelOthersOption('start')"
                  *ngIf="canEditForm">
                  <mat-icon>search</mat-icon>
                  <span>Search Directory Instead</span>
                </button>
              </div>

              <div class="endpoint-input-layout" [class.custom-port-active]="startIsOthers">
                <!-- Standard Port Name Field -->
                <mat-form-field appearance="outline" class="endpoint-port-field" *ngIf="!startIsOthers">
                  <mat-label>Departure Port Name</mat-label>
                  <input matInput formControlName="startPort" placeholder="Search departure port (e.g. Rotterdam)"
                    (input)="filterPortNames($event, 'start')" (blur)="onPortBlur('start')"
                    [matAutocomplete]="startPortAuto" [ngClass]="{ 'error-field': isStartErrorVisible() }" />
                  <mat-icon matSuffix class="field-suffix-icon">location_on</mat-icon>
                  <mat-autocomplete #startPortAuto="matAutocomplete" panelClass="voyage-dropdown-panel"
                    [displayWith]="displayPortName" (optionSelected)="onPortOptionSelected($event, 'start')">
                    <mat-option *ngFor="let suggestion of startPortSuggestions" [value]="suggestion.port_name">
                      <div class="autocomplete-option-row">
                        <span class="option-title">{{ suggestion.port_name }}</span>
                        <span class="option-country" *ngIf="suggestion.country"> {{ suggestion.country }}</span>
                      </div>
                    </mat-option>
                    <mat-option *ngIf="shouldShowOthersOption('start')" value="Others" class="others-custom-option">
                      <div class="autocomplete-option-row others-option-row">
                        <div class="others-title-wrap">
                          <mat-icon class="others-icon">add_location_alt</mat-icon>
                          <span class="option-title">Others (Port Not In List)</span>
                        </div>
                        <span class="option-country others-hint">Specify name & coordinates / UN/LOCODE</span>
                      </div>
                    </mat-option>
                  </mat-autocomplete>
                  <mat-hint *ngIf="lastPortAutoPopulated" class="auto-fill-hint">
                    <mat-icon class="hint-icon">auto_awesome</mat-icon> Auto-filled from last arrival: {{
                    lastPortDetails?.port }} ({{ lastPortDetails?.reportDate }})
                  </mat-hint>
                </mat-form-field>

                <!-- Custom Port Name Field (When Others is selected) -->
                <div class="custom-port-pair" *ngIf="startIsOthers">
                  <mat-form-field appearance="outline" class="endpoint-port-field">
                    <mat-label>Custom Departure Port Name</mat-label>
                    <input matInput formControlName="startCustomPortName"
                      placeholder="Enter custom port name (e.g. Terminal Alpha)" required
                      [ngClass]="{ 'error-field': isStartErrorVisible() }" />
                    <mat-icon matSuffix class="field-suffix-icon">edit_location</mat-icon>
                  </mat-form-field>

                  <mat-form-field appearance="outline" class="unlocode-field">
                    <mat-label>UN/LOCODE (Optional)</mat-label>
                    <input matInput formControlName="startUnlocode" placeholder="e.g. NLRTM" />
                  </mat-form-field>
                </div>

                <!-- Separator: OR for standard, AND for Others -->
                <div class="or-separator" *ngIf="!startIsOthers">
                  <span class="or-line-dotted"></span>
                  <span class="or-badge">OR</span>
                  <span class="or-line-dotted"></span>
                </div>
                <div class="and-separator" *ngIf="startIsOthers"
                  matTooltip="Coordinates or UN/LOCODE required with custom port">
                  <span class="and-line-dotted"></span>
                  <span class="and-badge">AND / OR</span>
                  <span class="and-line-dotted"></span>
                </div>

                <!-- Coordinates Fields -->
                <div class="coordinates-grid">
                  <div class="coord-pair">
                    <mat-form-field appearance="outline" class="coord-input-field">
                      <mat-label>Latitude</mat-label>
                      <input matInput formControlName="startLat" placeholder="e.g. 51.9244"
                        [ngClass]="{ 'error-field': isStartErrorVisible() }" />
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="coord-dir-field">
                      <mat-label>Dir</mat-label>
                      <mat-select formControlName="startLatDir" panelClass="voyage-dropdown-panel">
                        <mat-option value="N">N</mat-option>
                        <mat-option value="S">S</mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>

                  <div class="coord-pair">
                    <mat-form-field appearance="outline" class="coord-input-field">
                      <mat-label>Longitude</mat-label>
                      <input matInput formControlName="startLong" placeholder="e.g. 4.4777"
                        [ngClass]="{ 'error-field': isStartErrorVisible() }" />
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="coord-dir-field">
                      <mat-label>Dir</mat-label>
                      <mat-select formControlName="startLongDir" panelClass="voyage-dropdown-panel">
                        <mat-option value="E">E</mat-option>
                        <mat-option value="W">W</mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>
                </div>
              </div>

              <div class="error-text" *ngIf="isStartErrorVisible()">
                <mat-icon class="err-icon">error_outline</mat-icon>
                <span *ngIf="!startIsOthers">Please enter Departure Port name OR both Latitude & Longitude</span>
                <span *ngIf="startIsOthers">For custom port 'Others', enter Port Name AND (Coordinates OR
                  UN/LOCODE)</span>
              </div>
            </div>
          </div>

          <!-- 3.2 REORDERABLE STOPS (WAYPOINTS + DESTINATION) DROP LIST -->
          <div cdkDropList class="movable-stops-drop-list" (cdkDropListDropped)="dropRouteStop($event)">

            <!-- WAYPOINTS (ITINERARY) -->
            <div *ngFor="let item of itinerary.controls; let i = index" [formGroup]="$any(item)"
              class="timeline-stop-card mid-stop-card" [class.glide-in-from-below]="movedUpIndex === i"
              [class.glide-in-from-above]="movedDownIndex === i" cdkDrag>

              <div *cdkDragPreview class="drag-preview-box">
                <div class="preview-header">
                  <span class="preview-badge">Waypoint {{ i + 1 }}</span>
                  <span class="preview-title">{{ item.get('port')?.value || item.get('customPortName')?.value ||
                    'Waypoint' }}</span>
                </div>
                <div class="preview-op">Op: {{ item.get('operation')?.value || 'Discharging' }}</div>
              </div>

              <div *cdkDragPlaceholder class="drag-placeholder-box">
                <mat-icon>open_with</mat-icon>
                <span>Drop waypoint here</span>
              </div>

              <!-- Reorder Action Column -->
              <div class="stop-reorder-column" *ngIf="canEditForm">
                <div class="drag-grip-handle" cdkDragHandle matTooltip="Click & drag to reorder waypoint"
                  matTooltipPosition="above">
                  <mat-icon class="grip-icon">drag_indicator</mat-icon>
                </div>

                <div class="arrow-btn-group">
                  <button type="button" class="step-arrow-btn" [disabled]="isMoveUpDisabled(i)" (click)="moveStopUp(i)"
                    matTooltip="Move up" matTooltipPosition="left">
                    <mat-icon>keyboard_arrow_up</mat-icon>
                  </button>
                  <button type="button" class="step-arrow-btn" [disabled]="isMoveDownDisabled(i)"
                    (click)="moveStopDown(i)" matTooltip="Move down" matTooltipPosition="left">
                    <mat-icon>keyboard_arrow_down</mat-icon>
                  </button>
                </div>
              </div>

              <!-- Stop Node Marker -->
              <div class="stop-node-column">
                <div class="node-marker mid-marker">
                  <span class="node-number">{{ i + 1 }}</span>
                </div>
                <div class="node-connector-line"></div>
              </div>

              <!-- Stop Body -->
              <div class="stop-body-container">
                <div class="stop-header-row">
                  <div class="stop-badge-group">
                    <span class="stop-badge mid-badge">
                      <mat-icon class="badge-icon">pin_drop</mat-icon>
                      WAYPOINT {{ i + 1 }}
                    </span>
                    <span class="stop-title">Intermediate Waypoint</span>
                  </div>

                  <div class="stop-header-actions" *ngIf="canEditForm">
                    <button type="button" class="remove-stop-btn" (click)="removeItinerary(i)">
                      <mat-icon>delete_outline</mat-icon>
                      <span>Remove Waypoint</span>
                    </button>
                  </div>
                </div>

                <!-- Custom Waypoint Banner -->
                <div class="custom-port-active-bar mid-port-custom-bar" *ngIf="item.get('isOthers')?.value">
                  <div class="custom-port-tag">
                    <mat-icon class="custom-port-icon">add_location_alt</mat-icon>
                    <span class="custom-port-title">Custom Waypoint:</span>
                    <span class="custom-port-desc">Custom Port Name required &plus; (Coordinates <strong>OR</strong>
                      UN/LOCODE required)</span>
                  </div>
                  <button type="button" class="back-to-search-btn" (click)="cancelMidPortOthers(i)" *ngIf="canEditForm">
                    <mat-icon>search</mat-icon>
                    <span>Search Directory Instead</span>
                  </button>
                </div>

                <!-- Standard Waypoint Fields (When NOT Others) -->
                <div class="form-grid grid-3-col mid-fields-grid" *ngIf="!item.get('isOthers')?.value">
                  <!-- Port Name with Autocomplete (Standard) -->
                  <mat-form-field appearance="outline" class="voyage-form-field">
                    <mat-label>Waypoint Port Name</mat-label>
                    <input matInput formControlName="port" required placeholder="Enter intermediate waypoint port"
                      (input)="filterMidPortNames($event, i)" [matAutocomplete]="midPortAuto" />
                    <mat-icon matSuffix class="field-suffix-icon">location_on</mat-icon>
                    <mat-autocomplete #midPortAuto="matAutocomplete" panelClass="voyage-dropdown-panel"
                      [displayWith]="displayPortName" (optionSelected)="onMidPortOptionSelected($event, i)">
                      <mat-option *ngFor="let suggestion of getMidPortSuggestions(i)" [value]="suggestion.port_name">
                        <div class="autocomplete-option-row">
                          <span class="option-title">{{ suggestion.port_name }}</span>
                          <span class="option-country" *ngIf="suggestion.country">{{ suggestion.country }}</span>
                        </div>
                      </mat-option>
                      <mat-option *ngIf="shouldShowOthersOption(i)" value="Others" class="others-custom-option">
                        <div class="autocomplete-option-row others-option-row">
                          <div class="others-title-wrap">
                            <mat-icon class="others-icon">add_location_alt</mat-icon>
                            <span class="option-title">Others (Port Not In List)</span>
                          </div>
                          <span class="option-country others-hint">Specify name & coordinates / UN/LOCODE</span>
                        </div>
                      </mat-option>
                    </mat-autocomplete>
                    <mat-error *ngIf="item.get('port')?.invalid">
                      Waypoint port is required
                    </mat-error>
                  </mat-form-field>

                  <!-- Operation Type -->
                  <mat-form-field appearance="outline" class="voyage-form-field">
                    <mat-label>Operation Type</mat-label>
                    <mat-select formControlName="operation" panelClass="voyage-dropdown-panel" required>
                      <mat-option *ngFor="let op of operationOptions" [value]="op">
                        {{ op }}
                      </mat-option>
                    </mat-select>
                    <mat-error *ngIf="item.get('operation')?.hasError('required')">
                      Operation is required
                    </mat-error>
                  </mat-form-field>

                  <!-- Priority -->
                  <mat-form-field appearance="outline" class="voyage-form-field">
                    <mat-label>Priority</mat-label>
                    <mat-select formControlName="priority" panelClass="voyage-dropdown-panel">
                      <mat-option *ngFor="let p of priorityOptions" [value]="p">
                        {{ p }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>

                  <!-- Port Stay -->
                  <mat-form-field appearance="outline" class="voyage-form-field">
                    <mat-label>Port Stay (Hours)</mat-label>
                    <input matInput type="number" min="0" max="168" step="0.5" formControlName="portStay"
                      placeholder="e.g. 24 (Max 168h)" (keydown)="preventNonNumeric($event)" />
                    <span matSuffix class="field-unit-suffix">hrs</span>
                    <mat-error *ngIf="item.get('portStay')?.hasError('min')">
                      Must be 0 or positive
                    </mat-error>
                    <mat-error *ngIf="item.get('portStay')?.hasError('max')">
                      Cannot exceed 168 hours (7 days)
                    </mat-error>
                  </mat-form-field>

                  <!-- ETA Date & Time (UTC) -->
                  <div class="waypoint-date-time-group">
                    <mat-form-field appearance="outline" class="voyage-form-field eta-date-field">
                      <mat-label>ETA Date (UTC)</mat-label>
                      <input matInput [matDatepicker]="midEtaPicker" formControlName="eta" placeholder="Select date"
                        [min]="minItineraryDateObj" readonly (click)="midEtaPicker.open()" />
                      <mat-datepicker-toggle matIconSuffix [for]="midEtaPicker">
                        <mat-icon matDatepickerToggleIcon class="calendar-toggle-icon">event</mat-icon>
                      </mat-datepicker-toggle>
                      <mat-datepicker #midEtaPicker panelClass="custom-voyage-datepicker"></mat-datepicker>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="voyage-form-field eta-time-field">
                      <mat-label>Time (UTC)</mat-label>
                      <input matInput type="time" formControlName="etaTime" />
                      <mat-icon matSuffix class="field-suffix-icon">schedule</mat-icon>
                    </mat-form-field>
                  </div>

                  <!-- Cancel Date (UTC) -->
                  <mat-form-field appearance="outline" class="voyage-form-field date-picker-field">
                    <mat-label>Laycan / Cancelling Date (UTC)</mat-label>
                    <input matInput [matDatepicker]="midCancelPicker" formControlName="cancelDate"
                      placeholder="Select date (UTC)" [min]="minItineraryDateObj" readonly
                      (click)="midCancelPicker.open()" />
                    <mat-datepicker-toggle matIconSuffix [for]="midCancelPicker">
                      <mat-icon matDatepickerToggleIcon class="calendar-toggle-icon">event</mat-icon>
                    </mat-datepicker-toggle>
                    <mat-datepicker #midCancelPicker panelClass="custom-voyage-datepicker"></mat-datepicker>
                  </mat-form-field>
                </div>

                <!-- Custom Waypoint Fields (When Others IS Selected) -->
                <div class="custom-mid-stop-wrapper" *ngIf="item.get('isOthers')?.value">
                  <!-- Row 1: Custom Port Details + Coordinates Grid -->
                  <div class="custom-waypoint-top-row">
                    <mat-form-field appearance="outline" class="voyage-form-field custom-waypoint-name-field"
                      [id]="'mid-stop-' + i + '-custom-name'">
                      <mat-label>Custom Waypoint Name</mat-label>
                      <input matInput formControlName="customPortName" required
                        placeholder="Enter custom waypoint name" />
                      <mat-icon matSuffix class="field-suffix-icon">edit_location</mat-icon>
                      <mat-error *ngIf="item.get('customPortName')?.invalid">
                        Custom waypoint name is required
                      </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="voyage-form-field custom-waypoint-unlocode-field">
                      <mat-label>UN/LOCODE (Optional)</mat-label>
                      <input matInput formControlName="unlocode" placeholder="e.g. NLRTM" />
                    </mat-form-field>

                    <!-- Grouped Coordinates Grid -->
                    <div class="coordinates-grid">
                      <div class="coord-pair">
                        <mat-form-field appearance="outline" class="coord-input-field">
                          <mat-label>Latitude</mat-label>
                          <input matInput formControlName="lat" placeholder="e.g. 12.34" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="coord-dir-field">
                          <mat-label>Dir</mat-label>
                          <mat-select formControlName="latDir" panelClass="voyage-dropdown-panel">
                            <mat-option value="N">N</mat-option>
                            <mat-option value="S">S</mat-option>
                          </mat-select>
                        </mat-form-field>
                      </div>

                      <div class="coord-pair">
                        <mat-form-field appearance="outline" class="coord-input-field">
                          <mat-label>Longitude</mat-label>
                          <input matInput formControlName="long" placeholder="e.g. 56.78" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" class="coord-dir-field">
                          <mat-label>Dir</mat-label>
                          <mat-select formControlName="longDir" panelClass="voyage-dropdown-panel">
                            <mat-option value="E">E</mat-option>
                            <mat-option value="W">W</mat-option>
                          </mat-select>
                        </mat-form-field>
                      </div>
                    </div>
                  </div>

                  <!-- Row 2: Operational Parameters (3-Col Grid) -->
                  <div class="form-grid grid-3-col mid-operational-grid">
                    <!-- Operation Type -->
                    <mat-form-field appearance="outline" class="voyage-form-field">
                      <mat-label>Operation Type</mat-label>
                      <mat-select formControlName="operation" panelClass="voyage-dropdown-panel" required>
                        <mat-option *ngFor="let op of operationOptions" [value]="op">
                          {{ op }}
                        </mat-option>
                      </mat-select>
                      <mat-error *ngIf="item.get('operation')?.hasError('required')">
                        Operation is required
                      </mat-error>
                    </mat-form-field>

                    <!-- Priority -->
                    <mat-form-field appearance="outline" class="voyage-form-field">
                      <mat-label>Priority</mat-label>
                      <mat-select formControlName="priority" panelClass="voyage-dropdown-panel">
                        <mat-option *ngFor="let p of priorityOptions" [value]="p">
                          {{ p }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>

                    <!-- Port Stay -->
                    <mat-form-field appearance="outline" class="voyage-form-field">
                      <mat-label>Port Stay (Hours)</mat-label>
                      <input matInput type="number" min="0" max="168" step="0.5" formControlName="portStay"
                        placeholder="e.g. 24 (Max 168h)" (keydown)="preventNonNumeric($event)" />
                      <span matSuffix class="field-unit-suffix">hrs</span>
                      <mat-error *ngIf="item.get('portStay')?.hasError('min')">
                        Must be 0 or positive
                      </mat-error>
                      <mat-error *ngIf="item.get('portStay')?.hasError('max')">
                        Cannot exceed 168 hours (7 days)
                      </mat-error>
                    </mat-form-field>

                    <!-- ETA Date & Time (UTC) -->
                    <div class="waypoint-date-time-group">
                      <mat-form-field appearance="outline" class="voyage-form-field eta-date-field">
                        <mat-label>ETA Date (UTC)</mat-label>
                        <input matInput [matDatepicker]="customMidEtaPicker" formControlName="eta"
                          placeholder="Select date" [min]="minItineraryDateObj" readonly
                          (click)="customMidEtaPicker.open()" />
                        <mat-datepicker-toggle matIconSuffix [for]="customMidEtaPicker">
                          <mat-icon matDatepickerToggleIcon class="calendar-toggle-icon">event</mat-icon>
                        </mat-datepicker-toggle>
                        <mat-datepicker #customMidEtaPicker panelClass="custom-voyage-datepicker"></mat-datepicker>
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="voyage-form-field eta-time-field">
                        <mat-label>Time (UTC)</mat-label>
                        <input matInput type="time" formControlName="etaTime" />
                        <mat-icon matSuffix class="field-suffix-icon">schedule</mat-icon>
                      </mat-form-field>
                    </div>

                    <!-- Cancel Date (UTC) -->
                    <mat-form-field appearance="outline" class="voyage-form-field date-picker-field">
                      <mat-label>Laycan / Cancelling Date (UTC)</mat-label>
                      <input matInput [matDatepicker]="customMidCancelPicker" formControlName="cancelDate"
                        placeholder="Select date (UTC)" [min]="minItineraryDateObj" readonly
                        (click)="customMidCancelPicker.open()" />
                      <mat-datepicker-toggle matIconSuffix [for]="customMidCancelPicker">
                        <mat-icon matDatepickerToggleIcon class="calendar-toggle-icon">event</mat-icon>
                      </mat-datepicker-toggle>
                      <mat-datepicker #customMidCancelPicker panelClass="custom-voyage-datepicker"></mat-datepicker>
                    </mat-form-field>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3.3 REORDERABLE DESTINATION PORT -->
            <div class="timeline-stop-card destination-card"
              [class.glide-in-from-below]="movedUpIndex === itinerary.length"
              [class.glide-in-from-above]="movedDownIndex === itinerary.length" cdkDrag>

              <div *cdkDragPreview class="drag-preview-box">
                <div class="preview-header">
                  <span class="preview-badge dest-badge-preview">Destination Port</span>
                  <span class="preview-title">{{ voyageForm.get('endPort')?.value || 'Final Port' }}</span>
                </div>
                <div class="preview-op">Voyage Termination (ETA)</div>
              </div>

              <div *cdkDragPlaceholder class="drag-placeholder-box">
                <mat-icon>open_with</mat-icon>
                <span>Drop destination here</span>
              </div>

              <!-- Reorder Action Column -->
              <div class="stop-reorder-column" *ngIf="canEditForm">
                <div class="drag-grip-handle" cdkDragHandle
                  matTooltip="Click & drag to reorder destination into a waypoint" matTooltipPosition="above">
                  <mat-icon class="grip-icon">drag_indicator</mat-icon>
                </div>

                <div class="arrow-btn-group">
                  <button type="button" class="step-arrow-btn" [disabled]="isMoveUpDisabled(itinerary.length)"
                    (click)="moveStopUp(itinerary.length)" matTooltip="Move up" matTooltipPosition="left">
                    <mat-icon>keyboard_arrow_up</mat-icon>
                  </button>
                  <button type="button" class="step-arrow-btn" [disabled]="true" matTooltip="End of route"
                    matTooltipPosition="left">
                    <mat-icon>keyboard_arrow_down</mat-icon>
                  </button>
                </div>
              </div>

              <!-- Stop Node Marker -->
              <div class="stop-node-column">
                <div class="node-marker destination-marker">
                  <mat-icon class="dest-icon">sports_score</mat-icon>
                </div>
              </div>

              <!-- Stop Body -->
              <div class="stop-body-container">
                <div class="stop-header-row">
                  <div class="stop-badge-group">
                    <span class="stop-badge dest-badge">
                      <mat-icon class="badge-icon">flag</mat-icon>
                      DESTINATION
                    </span>
                    <span class="stop-title">Final Destination Port</span>
                  </div>
                  <span class="stop-subtext">Voyage termination point</span>
                </div>

                <!-- Custom Port Banner for Destination -->
                <div class="custom-port-active-bar dest-custom-bar" *ngIf="endIsOthers">
                  <div class="custom-port-tag">
                    <mat-icon class="custom-port-icon">add_location_alt</mat-icon>
                    <span class="custom-port-title">Custom Destination Mode:</span>
                    <span class="custom-port-desc">Custom Port Name required &plus; (Coordinates <strong>OR</strong>
                      UN/LOCODE required)</span>
                  </div>
                  <button type="button" class="back-to-search-btn" (click)="cancelOthersOption('end')"
                    *ngIf="canEditForm">
                    <mat-icon>search</mat-icon>
                    <span>Search Directory Instead</span>
                  </button>
                </div>

                <div class="endpoint-input-layout" [class.custom-port-active]="endIsOthers">
                  <!-- Standard Port Name Field -->
                  <mat-form-field appearance="outline" class="endpoint-port-field" *ngIf="!endIsOthers">
                    <mat-label>Destination Port Name</mat-label>
                    <input matInput formControlName="endPort" placeholder="Search destination port (e.g. Singapore)"
                      (input)="filterPortNames($event, 'end')" (blur)="onPortBlur('end')"
                      [matAutocomplete]="endPortAuto" [ngClass]="{ 'error-field': isEndErrorVisible() }" />
                    <mat-icon matSuffix class="field-suffix-icon">location_on</mat-icon>
                    <mat-autocomplete #endPortAuto="matAutocomplete" panelClass="voyage-dropdown-panel"
                      [displayWith]="displayPortName" (optionSelected)="onPortOptionSelected($event, 'end')">
                      <mat-option *ngFor="let suggestion of endPortSuggestions" [value]="suggestion.port_name">
                        <div class="autocomplete-option-row">
                          <span class="option-title">{{ suggestion.port_name }}</span>
                          <span class="option-country" *ngIf="suggestion.country"> {{ suggestion.country }}</span>
                        </div>
                      </mat-option>
                      <mat-option *ngIf="shouldShowOthersOption('end')" value="Others" class="others-custom-option">
                        <div class="autocomplete-option-row others-option-row">
                          <div class="others-title-wrap">
                            <mat-icon class="others-icon">add_location_alt</mat-icon>
                            <span class="option-title">Others (Port Not In List)</span>
                          </div>
                          <span class="option-country others-hint">Specify name & coordinates / UN/LOCODE</span>
                        </div>
                      </mat-option>
                    </mat-autocomplete>
                  </mat-form-field>

                  <!-- Custom Port Name Field (When Others selected) -->
                  <div class="custom-port-pair" *ngIf="endIsOthers">
                    <mat-form-field appearance="outline" class="endpoint-port-field">
                      <mat-label>Custom Destination Port Name</mat-label>
                      <input matInput formControlName="endCustomPortName"
                        placeholder="Enter custom port name (e.g. Jurong Island Berth)" required
                        [ngClass]="{ 'error-field': isEndErrorVisible() }" />
                      <mat-icon matSuffix class="field-suffix-icon">edit_location</mat-icon>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="unlocode-field">
                      <mat-label>UN/LOCODE (Optional)</mat-label>
                      <input matInput formControlName="endUnlocode" placeholder="e.g. SGSIN" />
                    </mat-form-field>
                  </div>

                  <!-- Separator: OR for standard, AND for Others -->
                  <div class="or-separator" *ngIf="!endIsOthers">
                    <span class="or-line-dotted"></span>
                    <span class="or-badge">OR</span>
                    <span class="or-line-dotted"></span>
                  </div>
                  <div class="and-separator" *ngIf="endIsOthers"
                    matTooltip="Coordinates or UN/LOCODE required with custom port">
                    <span class="and-line-dotted"></span>
                    <span class="and-badge">AND / OR</span>
                    <span class="and-line-dotted"></span>
                  </div>

                  <!-- Coordinates Fields -->
                  <div class="coordinates-grid">
                    <div class="coord-pair">
                      <mat-form-field appearance="outline" class="coord-input-field">
                        <mat-label>Latitude</mat-label>
                        <input matInput formControlName="endLat" placeholder="e.g. 1.29027"
                          [ngClass]="{ 'error-field': isEndErrorVisible() }" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="coord-dir-field">
                        <mat-label>Dir</mat-label>
                        <mat-select formControlName="endLatDir" panelClass="voyage-dropdown-panel">
                          <mat-option value="N">N</mat-option>
                          <mat-option value="S">S</mat-option>
                        </mat-select>
                      </mat-form-field>
                    </div>

                    <div class="coord-pair">
                      <mat-form-field appearance="outline" class="coord-input-field">
                        <mat-label>Longitude</mat-label>
                        <input matInput formControlName="endLong" placeholder="e.g. 103.851959"
                          [ngClass]="{ 'error-field': isEndErrorVisible() }" />
                      </mat-form-field>
                      <mat-form-field appearance="outline" class="coord-dir-field">
                        <mat-label>Dir</mat-label>
                        <mat-select formControlName="endLongDir" panelClass="voyage-dropdown-panel">
                          <mat-option value="E">E</mat-option>
                          <mat-option value="W">W</mat-option>
                        </mat-select>
                      </mat-form-field>
                    </div>
                  </div>
                </div>

                <div class="error-text" *ngIf="isEndErrorVisible()">
                  <mat-icon class="err-icon">error_outline</mat-icon>
                  <span *ngIf="!endIsOthers">Please enter Destination Port name OR both Latitude & Longitude</span>
                  <span *ngIf="endIsOthers">For custom port 'Others', enter Port Name AND (Coordinates OR
                    UN/LOCODE)</span>
                </div>
              </div>
            </div>

          </div>

          <!-- Bottom Add Waypoint Action Banner -->
          <div class="add-mid-port-bottom-bar" *ngIf="canEditForm">
            <button type="button" mat-stroked-button class="add-mid-port-action-btn" (click)="addItinerary()">
              <mat-icon>add_circle_outline</mat-icon>
              <span>Add Another Waypoint</span>
            </button>
          </div>

        </div>
      </section>

      <!-- ================= 4. ROUTING PREFERENCES & CONSTRAINTS ================= -->
      <section class="section-card">
        <div class="section-header">
          <div class="section-header-left">
            <mat-icon class="section-icon">tune</mat-icon>
            <div>
              <h2 class="section-title">Routing Preferences & Remarks</h2>
              <p class="section-subtitle">Select safety corridors, ECA routing, and specific voyage instructions</p>
            </div>
          </div>
        </div>

        <div class="preferences-container">
          <div class="checkbox-options-grid">
            <label class="custom-checkbox-card avoid-ice-card" [class.checked]="voyageForm.get('avoidIce')?.value">
              <input type="checkbox" formControlName="avoidIce" />
              <div class="checkbox-card-body">
                <div class="check-icon-badge ice-badge">
                  <mat-icon class="check-icon">ac_unit</mat-icon>
                </div>
                <div class="check-texts">
                  <span class="check-title">Avoid Ice</span>
                  <span class="check-desc">Route clear of icebergs and polar ice limits</span>
                </div>
              </div>
            </label>

            <label class="custom-checkbox-card avoid-hra-card" [class.checked]="voyageForm.get('avoidHRA')?.value">
              <input type="checkbox" formControlName="avoidHRA" />
              <div class="checkbox-card-body">
                <div class="check-icon-badge hra-badge">
                  <mat-icon class="check-icon">shield</mat-icon>
                </div>
                <div class="check-texts">
                  <span class="check-title">Avoid High Risk Area (HRA)</span>
                  <span class="check-desc">Bypass piracy risk zones & high-risk corridors</span>
                </div>
              </div>
            </label>

            <label class="custom-checkbox-card seca-fuel-card" [class.checked]="voyageForm.get('secaFuel')?.value">
              <input type="checkbox" formControlName="secaFuel" />
              <div class="checkbox-card-body">
                <div class="check-icon-badge seca-badge">
                  <mat-icon class="check-icon">eco</mat-icon>
                </div>
                <div class="check-texts">
                  <span class="check-title">SECA ECA Low Sulfur Fuel</span>
                  <span class="check-desc">Account for Emission Control Areas & fuel switch</span>
                </div>
              </div>
            </label>

            <label class="custom-checkbox-card tct-card" [class.checked]="voyageForm.get('tct')?.value">
              <input type="checkbox" formControlName="tct" />
              <div class="checkbox-card-body">
                <div class="check-icon-badge tct-badge">
                  <mat-icon class="check-icon">av_timer</mat-icon>
                </div>
                <div class="check-texts">
                  <span class="check-title">Time Charter Trip (TCT)</span>
                  <span class="check-desc">Charter party terms evaluation</span>
                </div>
              </div>
            </label>
          </div>

          <div class="remarks-box-wrapper">
            <mat-form-field appearance="outline" class="voyage-form-field remarks-field">
              <mat-label>Routing Instructions / Operator Remarks</mat-label>
              <textarea matInput formControlName="routingRemarks" rows="3"
                placeholder="e.g. Master requests to stay at least 30nm off Madagascar. Avoid winter North Atlantic track if wave height exceeds 5m."></textarea>
              <mat-icon matSuffix class="field-suffix-icon">notes</mat-icon>
            </mat-form-field>
          </div>

          <!-- Optional Route CSV / Waypoint Upload Area -->
          <div class="route-csv-upload-section">
            <div class="csv-section-header">
              <div class="csv-title-wrap">
                <mat-icon class="csv-header-icon">table_chart</mat-icon>
                <div>
                  <div class="csv-title-badge-row">
                    <h4 class="csv-section-title">Route Waypoints / Track File</h4>
                    <!-- <span class="optional-pill">Optional</span> -->
                    <button type="button" class="csv-demo-template-link" (click)="downloadDemoCsv($event)"
                      matTooltip="Download sample waypoint CSV template">
                      <mat-icon class="demo-link-icon">sim_card_download</mat-icon>
                      <span>Download Sample Template</span>Optional
                    </button>
                  </div>
                  <p class="csv-section-desc">
                    If you have an existing voyage track, passage plan, or waypoint CSV from your ECDIS / charter party,
                    upload it here.
                  </p>
                  <div class="csv-format-notice">
                    <mat-icon class="notice-icon">info_outline</mat-icon>
                    <span>
                      <strong>Format Notice:</strong> All waypoint file formats are accepted. Uploading in the
                      <strong>Sample Template format</strong> (with coordinates, waypoint names, speed, etc.) enables
                      instant interactive route map preview.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Drag and drop zone (When no file uploaded) -->
            <div class="csv-dropzone" *ngIf="!uploadedCsvFileName" [class.drag-over]="isDraggingCsvOver"
              [class.disabled-dropzone]="!canEditForm" (dragover)="onCsvDragOver($event)"
              (dragleave)="onCsvDragLeave($event)" (drop)="onCsvFileDropped($event)" (click)="csvFileInput.click()">
              <input #csvFileInput type="file" accept=".csv,.txt,.xlsx,.xls" class="hidden-file-input"
                (change)="onCsvFileSelected($event)" [disabled]="!canEditForm" />

              <div class="dropzone-content">
                <div class="dropzone-icon-circle">
                  <mat-icon class="dropzone-icon">{{ isDraggingCsvOver ? 'file_download' : 'cloud_upload' }}</mat-icon>
                </div>
                <div class="dropzone-texts">
                  <p class="dropzone-main-text">
                    <strong class="browse-link">Click to browse</strong> or drag and drop your route CSV file here
                  </p>
                  <p class="dropzone-sub-text">Supports CSV, TXT, Excel waypoint lists (Max 10MB)</p>
                </div>
              </div>
            </div>

            <!-- Attached File Card (When file uploaded or loaded from draft) -->
            <div class="attached-file-card" *ngIf="uploadedCsvFileName">
              <div class="file-card-left">
                <div class="file-icon-box">
                  <mat-icon>description</mat-icon>
                </div>
                <div class="file-meta">
                  <div class="file-name-row">
                    <span class="file-name" [title]="uploadedCsvFileName">{{ uploadedCsvFileName }}</span>
                    <span class="file-badge" *ngIf="uploadedCsvRowCount > 0">{{ uploadedCsvRowCount }} waypoints</span>
                    <span class="map-badge" *ngIf="parsedCsvWaypoints.length > 0">
                      <mat-icon class="badge-icon">map</mat-icon>
                      Map Preview Available ({{ parsedCsvWaypoints.length }} valid points)
                    </span>
                  </div>
                  <span class="file-size">{{ uploadedCsvFileSize || 'Attached File' }}</span>
                </div>
              </div>

              <div class="file-card-actions">
                <button type="button" mat-stroked-button class="file-action-btn map-toggle-btn" 
                  *ngIf="parsedCsvWaypoints.length > 0"
                  (click)="toggleCsvMap()" 
                  [matTooltip]="isCsvMapExpanded ? 'Hide interactive route map' : 'Show interactive route map'">
                  <mat-icon>{{ isCsvMapExpanded ? 'visibility_off' : 'visibility' }}</mat-icon>
                  <span>{{ isCsvMapExpanded ? 'Hide Map' : 'View Map' }}</span>
                </button>
                <button type="button" mat-stroked-button class="file-action-btn download-btn"
                  (click)="downloadCurrentCsv($event)" matTooltip="Download attached file">
                  <mat-icon>download</mat-icon>
                  <span>Download</span>
                </button>
                <button type="button" mat-icon-button class="file-action-btn remove-btn" (click)="removeCsvFile($event)"
                  *ngIf="canEditForm" matTooltip="Remove file">
                  <mat-icon>delete_outline</mat-icon>
                </button>
              </div>
            </div>

            <!-- Interactive Route Map Preview from Uploaded CSV -->
            <div class="csv-route-map-wrapper" 
                 *ngIf="uploadedCsvFileName && parsedCsvWaypoints.length > 0"
                 [hidden]="!isCsvMapExpanded">
              <div class="map-wrapper-header">
                <div class="map-header-left">
                  <mat-icon class="map-pin-icon">explore</mat-icon>
                  <span class="map-title">Route Waypoints Track Preview</span>
                  <span class="map-points-pill">{{ parsedCsvWaypoints.length }} plotted points</span>
                </div>
                <div class="map-header-right">
                  <span class="map-hint">Plotted directly from attached {{ uploadedCsvFileName }}</span>
                </div>
              </div>
              <div id="csv-route-preview-map" class="csv-leaflet-map"></div>
            </div>
          </div>

        </div>
      </section>

      <!-- ================= 5. FINANCIAL PARAMETERS ================= -->
      <mat-expansion-panel class="custom-expansion-panel" [expanded]="panelFinancialsExpanded"
        (opened)="panelFinancialsExpanded = true" (closed)="panelFinancialsExpanded = false">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon class="panel-header-icon">monetization_on</mat-icon>
            <span>Financial & Commercial Parameters</span>
          </mat-panel-title>
          <mat-panel-description>
            Daily charter rate, EU ETS, and bunker fuel prices
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="form-grid grid-2-col">
          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Daily Charter Rate ($/day)</mat-label>
            <input matInput formControlName="dailyRate" type="number" required placeholder="e.g. 15000"
              (keydown)="preventNonNumeric($event)" />
            <mat-icon matPrefix class="currency-prefix">attach_money</mat-icon>
            <mat-error *ngIf="voyageForm.get('dailyRate')?.invalid">
              Daily charter rate is required
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>EU ETS Carbon Price ($/tonne CO₂)</mat-label>
            <input matInput formControlName="etsPrice" type="number" placeholder="e.g. 75"
              (keydown)="preventNonNumeric($event)" />
            <mat-icon matPrefix class="currency-prefix">attach_money</mat-icon>
          </mat-form-field>
        </div>

        <div class="bunkers-section-title">
          <h4>Bunker Fuel Prices ($/MT)</h4>
          <span class="bunker-subtext">Select fuels to specify pricing.</span>
        </div>
        <div class="form-grid grid-4-col">
          <div class="bunker-input-item" [class.active-fuel]="voyageForm.get('hsfoCheck')?.value">
            <label class="bunker-check-label">
              <input type="checkbox" formControlName="hsfoCheck">
              <span>HSFO Price</span>
            </label>
            <mat-form-field appearance="outline" class="bunker-field">
              <input matInput formControlName="hsfo" type="number" placeholder="Price ($/MT)"
                (keydown)="preventNonNumeric($event)" />
              <span matSuffix class="bunker-currency-suffix">$/MT</span>
            </mat-form-field>
          </div>

          <div class="bunker-input-item" [class.active-fuel]="voyageForm.get('vlsfoCheck')?.value">
            <label class="bunker-check-label">
              <input type="checkbox" formControlName="vlsfoCheck">
              <span>VLSFO Price</span>
            </label>
            <mat-form-field appearance="outline" class="bunker-field">
              <input matInput formControlName="vlsfo" type="number" placeholder="Price ($/MT)"
                (keydown)="preventNonNumeric($event)" />
              <span matSuffix class="bunker-currency-suffix">$/MT</span>
            </mat-form-field>
          </div>

          <div class="bunker-input-item" [class.active-fuel]="voyageForm.get('lsmgoCheck')?.value">
            <label class="bunker-check-label">
              <input type="checkbox" formControlName="lsmgoCheck">
              <span>LSMGO Price</span>
            </label>
            <mat-form-field appearance="outline" class="bunker-field">
              <input matInput formControlName="lsmgo" type="number" placeholder="Price ($/MT)"
                (keydown)="preventNonNumeric($event)" />
              <span matSuffix class="bunker-currency-suffix">$/MT</span>
            </mat-form-field>
          </div>

          <div class="bunker-input-item" [class.active-fuel]="voyageForm.get('biofuelCheck')?.value">
            <label class="bunker-check-label">
              <input type="checkbox" formControlName="biofuelCheck">
              <span>Biofuel Price</span>
            </label>
            <mat-form-field appearance="outline" class="bunker-field">
              <input matInput formControlName="biofuel" type="number" placeholder="Price ($/MT)"
                (keydown)="preventNonNumeric($event)" />
              <span matSuffix class="bunker-currency-suffix">$/MT</span>
            </mat-form-field>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- ================= 6. OPTIMIZATION & SPEED PROFILES ================= -->
      <mat-expansion-panel class="custom-expansion-panel" [expanded]="panelSpeedExpanded"
        (opened)="panelSpeedExpanded = true" (closed)="panelSpeedExpanded = false">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon class="panel-header-icon">speed</mat-icon>
            <span>Optimization & Speed Profiles</span>
          </mat-panel-title>
          <mat-panel-description>
            Instructed Speed, Fixed ETA, or Speed/Consumption sets
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="optimization-settings-layout">
          <!-- Row 1: Optimization Mode + Instructed Speed + Instructed Fuel Consumption -->
          <div class="form-grid grid-3-col">
            <mat-form-field appearance="outline" class="voyage-form-field">
              <mat-label>Optimization Mode</mat-label>
              <mat-select formControlName="optimizationType" panelClass="voyage-dropdown-panel">
                <mat-option *ngFor="let opt of optimizationTypeOptions" [value]="opt">
                  {{ opt }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="voyage-form-field"
              *ngIf="voyageForm.get('optimizationType')?.value === 'Instructed Speed'">
              <mat-label>Instructed Speed (knots)</mat-label>
              <input matInput type="number" formControlName="speed" placeholder="e.g. 12.5"
                (keydown)="preventNonNumeric($event)" />
              <mat-icon matSuffix class="field-suffix-icon">speed</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="voyage-form-field"
              *ngIf="voyageForm.get('optimizationType')?.value === 'Instructed Speed'">
              <mat-label>Instructed Consumption (MT/day)</mat-label>
              <input matInput type="number" formControlName="consumption" placeholder="e.g. 24.5"
                (keydown)="preventNonNumeric($event)" />
              <mat-icon matSuffix class="field-suffix-icon">local_gas_station</mat-icon>
            </mat-form-field>
          </div>

          <!-- Row 2: Speed Remarks as Multiline Textbox -->
          <div class="speed-remarks-row">
            <mat-form-field appearance="outline" class="voyage-form-field full-width">
              <mat-label>Speed & Consumption Remarks</mat-label>
              <textarea matInput formControlName="speedRemarks" rows="2"
                placeholder="e.g. Eco speed in ballast / Maintain minimum 12.0 kn in calm seas / Weather adjusted consumption expectations..."></textarea>
              <mat-icon matSuffix class="field-suffix-icon">notes</mat-icon>
            </mat-form-field>
          </div>
        </div>

        <!-- Speed & Consumption Table with Clean Alignment -->
        <div class="speed-sets-container">
          <div class="speed-sets-header">
            <div class="speed-sets-title-wrap">
              <h4>Speed & Consumption Curves</h4>
              <p class="speed-subtext">Define baseline speed and daily fuel burn profile for Laden and Ballast
                conditions.</p>
            </div>
            <button type="button" mat-stroked-button class="add-speed-btn" (click)="addSpeedSet()" *ngIf="canEditForm">
              <mat-icon>add</mat-icon>
              <span>Add Curve</span>
            </button>
          </div>

          <div formArrayName="speedSets" class="speed-sets-list">
            <div class="speed-table-header-row">
              <span class="th-index">#</span>
              <span class="th-speed">Speed (knots) *</span>
              <span class="th-consumption">Consumption (MT/day) *</span>
              <span class="th-action">Action</span>
            </div>

            <div *ngFor="let set of speedSets.controls; let s = index" [formGroupName]="s" class="speed-set-row">
              <span class="set-index">#{{ s + 1 }}</span>

              <mat-form-field appearance="outline" class="set-input set-speed-input">
                <mat-label>Speed (kn)</mat-label>
                <input matInput type="number" formControlName="speed" required placeholder="e.g. 12.5"
                  (keydown)="preventNonNumeric($event)" />
              </mat-form-field>

              <mat-form-field appearance="outline" class="set-input set-consumption-input">
                <mat-label>Fuel Burn (MT/d)</mat-label>
                <input matInput type="number" formControlName="consumption" required placeholder="e.g. 25.0"
                  (keydown)="preventNonNumeric($event)" />
              </mat-form-field>

              <div class="set-action-col">
                <button type="button" mat-icon-button class="delete-curve-btn" (click)="removeSet(s)"
                  [disabled]="speedSets.length <= 1 || !canEditForm" matTooltip="Delete this curve">
                  <mat-icon>delete_outline</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- ================= 6.2 SAFETY MARGINS ================= -->
      <mat-expansion-panel class="custom-expansion-panel" [expanded]="panelSafetyExpanded"
        (opened)="panelSafetyExpanded = true" (closed)="panelSafetyExpanded = false">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon class="panel-header-icon">security</mat-icon>
            <span>Safety Margins & Draft Constraints</span>
          </mat-panel-title>
        </mat-expansion-panel-header>

        <div class="form-grid grid-3-col">
          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Under Keel Clearance - UKC (m)</mat-label>
            <input matInput type="number" formControlName="underKeel" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Air Draft (m)</mat-label>
            <input matInput type="number" formControlName="airDraft" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Aft Draft (m)</mat-label>
            <input matInput formControlName="aftDraft" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Forward Draft (m)</mat-label>
            <input matInput formControlName="forwardDraft" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Mean Draft (m)</mat-label>
            <input matInput formControlName="beta" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Distance off Land / Hazards (nm)</mat-label>
            <input matInput formControlName="hazardDistance" (keydown)="preventNonNumeric($event)">
          </mat-form-field>
        </div>

        <div class="margin-checkbox-row">
          <label class="custom-checkbox">
            <input type="checkbox" formControlName="weatherRestriction">
            <span>Ignore UKC, Distance off, and Weather restrictions for arrival and departure ports</span>
          </label>
        </div>
      </mat-expansion-panel>

      <!-- ================= 6.3 WEATHER SAFETY ================= -->
      <mat-expansion-panel class="custom-expansion-panel" [expanded]="panelWeatherExpanded"
        (opened)="panelWeatherExpanded = true" (closed)="panelWeatherExpanded = false">
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon class="panel-header-icon">cloud</mat-icon>
            <span>Weather Safety Thresholds</span>
          </mat-panel-title>
        </mat-expansion-panel-header>

        <div class="form-grid grid-3-col">
          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Max Wind Speed (knots)</mat-label>
            <input matInput type="number" formControlName="maxWind" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Max Significant Wave Height (m)</mat-label>
            <input matInput type="number" formControlName="maxWave" (keydown)="preventNonNumeric($event)">
          </mat-form-field>

          <mat-form-field appearance="outline" class="voyage-form-field">
            <mat-label>Distance from Storm / Hazard Stop (nm)</mat-label>
            <input matInput formControlName="distanceFromStop">
          </mat-form-field>
        </div>
      </mat-expansion-panel>

      <!-- Operator Audit Note (Shown when Meraki Ops is editing an active/finalized request, NOT a draft) -->
      <section class="section-card meraki-audit-note-card"
        *ngIf="isMerakiLogin && isEditMode && canEditForm && editingItemStatus !== 'Draft'">
        <div class="section-header">
          <div class="section-header-left">
            <mat-icon class="section-icon meraki-icon">history_edu</mat-icon>
            <div>
              <h2 class="section-title">Meraki Operations Edit Log</h2>
              <p class="section-subtitle">Record the rationale for field corrections or route adjustments (Logged to
                Timeline)</p>
            </div>
          </div>
        </div>
        <mat-form-field appearance="outline" class="voyage-form-field full-width-field">
          <mat-label>Reason for Editing / Operator Audit Note</mat-label>
          <input matInput [(ngModel)]="merakiEditRemarks" [ngModelOptions]="{standalone: true}"
            placeholder="e.g. Corrected port terminal spelling and updated eco speed to 13.0 knots per charterer note" />
          <mat-icon matSuffix class="field-suffix-icon">edit_note</mat-icon>
        </mat-form-field>
      </section>

      <!-- ================= 7. FOOTER ACTION BAR & RICH SUMMARY ================= -->
      <footer class="form-footer-bar">
        <div class="footer-summary-wrap">
          <div class="footer-summary-row">
            <span class="route-summary-text">
              <mat-icon class="footer-summary-icon">navigation</mat-icon>
              <strong>Route Summary:</strong>
              <span class="summary-chip origin-chip">Dep: {{ summaryRouteDeparture }}</span>
              <span class="summary-arrow">➔</span>
              <span class="summary-chip mid-chip" *ngIf="itinerary.length > 0">{{ itinerary.length }} Waypoint{{
                itinerary.length === 1 ? '' : 's' }}</span>
              <span class="summary-arrow" *ngIf="itinerary.length > 0">➔</span>
              <span class="summary-chip dest-chip">Dest: {{ summaryRouteDestination }}</span>
            </span>
          </div>

          <div class="footer-metrics-row">
            <span class="metric-pill" *ngIf="voyageForm.get('vesselName')?.value">
              <mat-icon>directions_boat</mat-icon>
              Vessel: <strong>{{ voyageForm.get('vesselName')?.value }}</strong>
            </span>
            <span class="metric-pill"
              *ngIf="voyageForm.get('departureDate')?.value || voyageForm.get('departureTime')?.value">
              <mat-icon>event</mat-icon>
              ETD: <strong>{{ formatIsoDateString(voyageForm.get('departureDate')?.value) || '' }}
                {{ voyageForm.get('departureTime')?.value ? voyageForm.get('departureTime')?.value + ' UTC' : ''
                }}</strong>
            </span>
            <span class="metric-pill" *ngIf="voyageForm.get('dailyRate')?.value">
              <mat-icon>attach_money</mat-icon>
              Charter: <strong>${{ voyageForm.get('dailyRate')?.value | number }}/day</strong>
            </span>
            <span class="metric-pill">
              <mat-icon>speed</mat-icon>
              Mode: <strong>{{ voyageForm.get('optimizationType')?.value }}</strong> ({{ speedSets.length }} Curve{{
              speedSets.length === 1 ? '' : 's' }})
            </span>
            <span class="metric-pill">
              <mat-icon>shield</mat-icon>
              Weather Margin: <strong>{{ voyageForm.get('weatherRestriction')?.value ? 'YES' : 'NO' }}</strong>
            </span>
            <span class="metric-pill" *ngIf="activeAvoidancesList.length > 0">
              <mat-icon>tune</mat-icon>
              Avoid: <strong>{{ activeAvoidancesList.join(', ') }}</strong>
            </span>
            <span class="metric-pill" *ngIf="activeBunkersCount > 0">
              <mat-icon>local_gas_station</mat-icon>
              Bunkers: <strong>{{ activeBunkersCount }} Configured</strong>
            </span>
          </div>
        </div>

        <div class="footer-right">
          <button type="button" mat-stroked-button class="draft-form-btn" (click)="saveAsDraft()"
            [disabled]="isSavingDraft || isSubmittingForm"
            *ngIf="canEditForm && (!isEditMode || editingItemStatus === 'Draft')">
            <mat-spinner diameter="16" class="btn-spinner" *ngIf="isSavingDraft"></mat-spinner>
            <mat-icon *ngIf="!isSavingDraft">save_as</mat-icon>
            <span>{{ isSavingDraft ? 'Saving Draft…' : 'Save as Draft' }}</span>
          </button>

          <button type="button" mat-stroked-button class="reset-form-btn" (click)="resetForm()"
            [disabled]="isSavingDraft || isSubmittingForm" *ngIf="canEditForm && !isEditMode">
            <mat-icon>refresh</mat-icon>
            <span>Reset</span>
          </button>

          <button type="button" mat-flat-button class="submit-request-btn" (click)="onSubmit()"
            [disabled]="isSavingDraft || isSubmittingForm" *ngIf="canEditForm">
            <mat-spinner diameter="16" class="btn-spinner submit-spinner" *ngIf="isSubmittingForm"></mat-spinner>
            <mat-icon *ngIf="!isSubmittingForm">{{ editingItemStatus === 'Edit Requested' ? 'published_with_changes' :
              (isEditMode && editingItemStatus !== 'Draft' ? 'save' : 'send') }}</mat-icon>
            <span>{{ isSubmittingForm ? 'Submitting…' : (editingItemStatus === 'Edit Requested' ? 'Resubmit Routing
              Request' : (isEditMode && editingItemStatus !== 'Draft' ? 'Save Changes' : (editingItemStatus === 'Draft'
              ? 'Submit Routing Request' : 'Submit Routing Request'))) }}</span>
          </button>
        </div>
      </footer>

    </form>
  </div>
</div>